import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { TableChangeParams, TableProps } from "./types";
import {
  FiltersInput,
  AsyncFiltersSelect,
  FilterButton,
  FilterDate,
} from "../Tailwind/Filters";
import FilterSelect from "../Tailwind/Filters/select";
import FilterDates from "../Tailwind/Filters/date";
import overrideClasses from "../../utils/overrideClasses";
import DownloadCSVButton from "../Tailwind/DownloadCSVButton";
import { EnumFilterType, getFilterFields, IFilter } from "../../utils/filter";
import { SortsSelector } from "./Sorts";
import { SearchableSortDirection } from "../../lib/ground-aws-graphql-core/api/graphql/types";
import images from "../../images";
import FilterBlock from "../Tailwind/Filters/events";
import { DEFAULT_LIMIT, formatISODateFn } from "../../utils/config";
import TableContainer from "./TableContainer";
import Head from "./Head";
import Body from "./Body";
import Foot from "./Foot";

export const ALL_FILTER = "ALL";
export const ALL_FILTER_NUMBER = -1;

export interface IFilterField<T> {
  type: string;
  value: T | null;
  current?: IFilter;
  options?: {
    value: string | number;
    label: string;
    code?: number;
  }[];
}

const Table = (props: TableProps): JSX.Element => {
  const {
    initialParams: tableInitialParams,
    head,
    headBgColor,
    headTextColor,
    body,
    noDataText,
    permissionEntity,
    onChange,
    exportData,
    loadOptions,
    className,
    paginationTotal,
    paginationLimit,
    limitChoiceEnabled,
    loading,
    setLoading,
    filterBlockTitle,
    date,
    onChangeDate,
    handleOrderElement,
    useFilterBlock = false,
  } = props;

  const intl = useIntl();

  const initialParams: TableChangeParams = tableInitialParams ?? {
    pageIndex: 0,
    type: "",
    filters: [],
    limit: paginationLimit || DEFAULT_LIMIT,
  };
  const [changeParams, setChangeParams] =
    useState<TableChangeParams>(initialParams);

  useEffect(() => {
    if (onChange && changeParams !== initialParams) {
      onChange(changeParams);
    }
  }, [changeParams]);

  const { values: filterFields, multiple } = useMemo(
    () => getFilterFields(intl, permissionEntity),
    [intl, permissionEntity]
  );

  const inputFilters = useMemo(
    () => filterFields.filter(f => f.type === EnumFilterType.INPUT),
    [filterFields]
  );
  const selectFiltersSync = useMemo(
    () =>
      filterFields.filter(
        f => f.type === EnumFilterType.SELECT && f.async === false
      ),
    [filterFields]
  );
  const selectFiltersAsync = useMemo(
    () =>
      filterFields.filter(
        f => f.type === EnumFilterType.SELECT && f.async === true
      ),
    [filterFields]
  );
  const dateFilters = useMemo(
    () => filterFields.filter(f => f.type === EnumFilterType.DATE),
    [filterFields]
  );

  const filterInput = inputFilters.length > 0 && !!onChange;
  const filterSelect = selectFiltersSync.length > 0 && !!onChange;
  const filterSelectAsync =
    selectFiltersAsync.length > 0 && !!onChange && loadOptions;
  const filterDate = dateFilters.length > 0;
  const hasFilter =
    filterInput || filterDate || filterSelect || filterSelectAsync;

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = { ...changeParams, pageIndex: page };
      setChangeParams(newParams);
      setLoading?.(true);
      onChange?.(newParams);
    },
    [changeParams, onChange, setLoading]
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      const newParams = { ...changeParams, pageIndex: 0, limit: newLimit };
      setChangeParams(newParams);
      setLoading?.(true);
      onChange?.(newParams);
    },
    [changeParams, onChange, setLoading]
  );

  const saveElementOrder = useCallback(
    async (id: string, ids: string[]) => {
      const newIndex = ids.findIndex(cId => cId === id);
      const beforeId = newIndex > 0 ? ids[newIndex - 1] : undefined;
      const afterId = newIndex < ids.length - 1 ? ids[newIndex + 1] : undefined;
      handleOrderElement?.(id, beforeId, afterId);
    },
    [handleOrderElement]
  );

  const { pageIndex, limit } = changeParams;

  const updateFilters = useCallback(
    (newFilter: IFilterField<string>, type: string) => {
      const filters = changeParams.filters
        ? [...changeParams.filters].filter(f => f.type !== type)
        : [];
      filters.push(newFilter);
      setChangeParams({ ...changeParams, pageIndex: 0, type, filters });
    },
    [changeParams]
  );

  return loading ? (
    <div className="loading" />
  ) : (
    <div className={overrideClasses(className)}>
      <div
        className={`flex items-center ${
          hasFilter ? "justify-between" : "justify-end"
        }`}
      >
        <div className="flex">
          {useFilterBlock ? (
            <>
              {date && onChangeDate && (
                <div className="mr-2">
                  <FilterDate
                    onChange={d => {
                      setChangeParams({ ...changeParams, pageIndex: 0 });
                      onChangeDate(d);
                    }}
                    date={date}
                    className="mt-4"
                  />
                </div>
              )}
              <FilterButton
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                isOpen={isFilterOpen}
                icon={isFilterOpen ? images.filterOn : images.filterOff}
                title={filterBlockTitle}
              />
            </>
          ) : (
            <>
              {dateFilters.map((filter, index) => (
                <Fragment key={index}>
                  <FilterDates
                    fields={[filter]}
                    filters={dateFilters}
                    selectedValue={filter.defaultValue as Date}
                    customInput={filter.customInput}
                    selectedFilters={changeParams.filters ?? []}
                    onChange={(type, target: Date) =>
                      updateFilters(
                        {
                          type,
                          value: target ? formatISODateFn(target) : null,
                          current: filter,
                        },
                        type
                      )
                    }
                    className="mr-2"
                  />
                </Fragment>
              ))}

              {filterSelect && (
                <FilterSelect
                  fields={selectFiltersSync}
                  selectedFilters={changeParams.filters}
                  onChange={(type, target) =>
                    updateFilters({ type, value: target }, type)
                  }
                  className="my-4 mr-2"
                />
              )}

              {filterSelectAsync && (
                <AsyncFiltersSelect
                  fields={selectFiltersAsync}
                  selectedFilters={changeParams.filters}
                  multiple={
                    multiple
                      ? multiple[`${EnumFilterType[EnumFilterType.SELECT]}`]
                      : false
                  }
                  onSelectChange={(type, target) =>
                    updateFilters({ type, value: target }, type)
                  }
                  className="my-4 mr-2"
                  loadOptions={loadOptions}
                />
              )}

              {filterInput && (
                <FiltersInput
                  fields={inputFilters}
                  selectedFilters={changeParams.filters}
                  onSelectChange={(e, fields) => {
                    const types = fields.map(f => f.value);
                    const filters = changeParams.filters
                      ? [...changeParams.filters].filter(
                          f => !types.includes(f.type)
                        )
                      : [];
                    setChangeParams({
                      ...changeParams,
                      pageIndex: 0,
                      type: e.target.value,
                      filters,
                    });
                  }}
                  onInputChange={(type, target) =>
                    updateFilters({ type, value: target }, type)
                  }
                />
              )}
            </>
          )}
        </div>

        <div className="flex">
          {permissionEntity && onChange && (
            <SortsSelector
              permissionEntity={permissionEntity}
              onChange={(field, direction) =>
                setChangeParams({
                  ...changeParams,
                  pageIndex: 0,
                  sort: {
                    field,
                    direction: SearchableSortDirection[direction],
                  },
                })
              }
            />
          )}

          {!!exportData?.onExport && exportData?.filename && (
            <DownloadCSVButton
              onClick={exportData.onExport}
              filename={exportData.filename}
              headers={exportData.headers || []}
            />
          )}
        </div>
      </div>

      {isFilterOpen && (
        <FilterBlock
          title={filterBlockTitle}
          permissionEntity={permissionEntity}
          changeParams={changeParams}
          onChange={(type, filters) =>
            setChangeParams({ ...changeParams, pageIndex: 0, type, filters })
          }
          loadOptions={loadOptions}
        />
      )}

      <TableContainer>
        <Head
          headArray={head}
          headBgColor={headBgColor}
          headTextColor={headTextColor}
        />
        <Body
          bodyArray={body}
          spanNoData={head.length}
          noDataText={noDataText}
          saveElementOrder={saveElementOrder}
          sortable={!!handleOrderElement}
        />
      </TableContainer>

      {paginationTotal !== undefined && (
        <Foot
          total={paginationTotal}
          limit={limit}
          forcePage={pageIndex}
          onPageChange={handlePageChange}
          limitChoiceEnabled={limitChoiceEnabled}
          onLimitChange={handleLimitChange}
          intl={intl}
        />
      )}
    </div>
  );
};

export default Table;
